import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import './style.css';
import '../../styles/modern-settings.css';
import { SidebarContext } from "../../AppRoutes";
import PermissionGate from "../../services/PermissionGate";

import { FaTruck } from "react-icons/fa";
import { MdStorefront } from "react-icons/md";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { SlActionUndo } from "react-icons/sl";

import { updateConfiguracoesEntrega, getEmpresaWithObjId } from "../../services/api";
import { toast } from "react-toastify";

const FormasTiposEntrega = ({ embedded = false, onSave, onBack }) => {
    const { t } = useTranslation('storeInfo');

    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);
    const empresaObjId = empresaParse._id;

    const { sidebar } = useContext(SidebarContext);
    const [isLoading, setIsLoading] = useState(true);
    const [entregaDisabled, setEntregaDisabled] = useState(false);
    const [retiradaDisabled, setRetiradaDisabled] = useState(false);
    const [savingEntrega, setSavingEntrega] = useState(false);
    const [savingRetirada, setSavingRetirada] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchEmpresa = async () => {
            setIsLoading(true);
            try {
                const empresaTemp = await getEmpresaWithObjId(empresaObjId);
                setEntregaDisabled(empresaTemp.data.empresa.entrega_disabled || false);
                setRetiradaDisabled(empresaTemp.data.empresa.retirada_disabled || false);
            } catch (error) {
                console.error("Erro ao buscar empresa:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchEmpresa();
    }, [empresaObjId]);

    // Handler para toggle de Entrega - salva automaticamente
    const handleEntregaToggle = async (e) => {
        const newValue = !e.target.checked; // checked = habilitado, disabled = !checked
        setSavingEntrega(true);

        try {
            await updateConfiguracoesEntrega(empresaObjId, newValue, retiradaDisabled);
            setEntregaDisabled(newValue);
            toast.success(
                newValue
                    ? t('toasts.deliveryDisabled', 'Entrega desabilitada')
                    : t('toasts.deliveryEnabled', 'Entrega habilitada')
            );
            if (onSave) onSave();
        } catch (error) {
            console.error("Erro ao salvar:", error);
            toast.error(t('toasts.deliverySettingsError', 'Erro ao salvar configuracoes'));
        } finally {
            setSavingEntrega(false);
        }
    };

    // Handler para toggle de Retirada - salva automaticamente
    const handleRetiradaToggle = async (e) => {
        const newValue = !e.target.checked; // checked = habilitado, disabled = !checked
        setSavingRetirada(true);

        try {
            await updateConfiguracoesEntrega(empresaObjId, entregaDisabled, newValue);
            setRetiradaDisabled(newValue);
            toast.success(
                newValue
                    ? t('toasts.pickupDisabled', 'Retirada desabilitada')
                    : t('toasts.pickupEnabled', 'Retirada habilitada')
            );
            if (onSave) onSave();
        } catch (error) {
            console.error("Erro ao salvar:", error);
            toast.error(t('toasts.deliverySettingsError', 'Erro ao salvar configuracoes'));
        } finally {
            setSavingRetirada(false);
        }
    };

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate("/");
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="modern-loading">
                <div className="modern-spinner"></div>
                <p>{t('loading', 'Carregando...')}</p>
            </div>
        );
    }

    // Conteudo do formulario
    const formContent = (
        <div className="entrega-modern-wrapper">
            {/* Card de Servico de Entrega */}
            <div className={`entrega-option-card ${savingEntrega ? 'saving' : ''}`}>
                <div className="entrega-option-content">
                    <div className={`entrega-option-icon ${!entregaDisabled ? 'active' : 'disabled'}`}>
                        <FaTruck />
                    </div>
                    <div className="entrega-option-text">
                        <h3>{t('deliveryTypes.deliveryOption.title', 'Servico de Entrega')}</h3>
                        <p>
                            {!entregaDisabled
                                ? t('deliveryTypes.deliveryOption.descriptionEnabled', 'Entrega habilitada - clientes podem solicitar entrega')
                                : t('deliveryTypes.deliveryOption.descriptionDisabled', 'Entrega desabilitada - clientes nao podem solicitar entrega')
                            }
                        </p>
                    </div>
                </div>
                <label className={`entrega-toggle ${savingEntrega ? 'loading' : ''}`}>
                    <input
                        type="checkbox"
                        checked={!entregaDisabled}
                        onChange={handleEntregaToggle}
                        disabled={savingEntrega || savingRetirada}
                    />
                    <span className="entrega-toggle-slider"></span>
                    {savingEntrega && <span className="entrega-toggle-spinner"></span>}
                </label>
            </div>

            {/* Card de Retirada */}
            <div className={`entrega-option-card ${savingRetirada ? 'saving' : ''}`}>
                <div className="entrega-option-content">
                    <div className={`entrega-option-icon ${!retiradaDisabled ? 'active' : 'disabled'}`}>
                        <MdStorefront />
                    </div>
                    <div className="entrega-option-text">
                        <h3>{t('deliveryTypes.pickupOption.title', 'Retirada no Local')}</h3>
                        <p>
                            {!retiradaDisabled
                                ? t('deliveryTypes.pickupOption.descriptionEnabled', 'Retirada habilitada - clientes podem retirar no local')
                                : t('deliveryTypes.pickupOption.descriptionDisabled', 'Retirada desabilitada - clientes nao podem retirar no local')
                            }
                        </p>
                    </div>
                </div>
                <label className={`entrega-toggle ${savingRetirada ? 'loading' : ''}`}>
                    <input
                        type="checkbox"
                        checked={!retiradaDisabled}
                        onChange={handleRetiradaToggle}
                        disabled={savingEntrega || savingRetirada}
                    />
                    <span className="entrega-toggle-slider"></span>
                    {savingRetirada && <span className="entrega-toggle-spinner"></span>}
                </label>
            </div>

            {/* Info Box */}
            <div className="entrega-info-box">
                <AiOutlineInfoCircle className="entrega-info-icon" />
                <div className="entrega-info-content">
                    <strong>{t('deliveryTypes.infoBox.title', 'Informacao Importante')}</strong>
                    <p>{t('deliveryTypes.infoBox.message', 'Ao desabilitar um servico, os clientes nao conseguirao mais seleciona-lo durante o pedido. Certifique-se de que pelo menos um tipo de servico esteja habilitado.')}</p>
                </div>
            </div>
        </div>
    );

    // Se estiver embedded, retorna conteudo com header estilizado (igual outras abas)
    if (embedded) {
        return (
            <div className="entrega-embedded-container">
                {/* Header com icone e titulo - Estilo igual Dados da Loja */}
                <div className="entrega-section-header">
                    <div className="entrega-header-content">
                        <div className="entrega-header-icon">
                            <FaTruck />
                        </div>
                        <div>
                            <h2>{t('deliveryTypes.title', 'Formas e Tipos de Entrega')}</h2>
                            <p>{t('deliveryTypes.subtitle', 'Configure quais tipos de servico sua loja oferece aos clientes')}</p>
                        </div>
                    </div>
                </div>
                {formContent}
            </div>
        );
    }

    // Modo normal com wrapper completo
    return (
        <PermissionGate permissions={['default']}>
            <div
                className="modern-settings-container"
                style={{
                    marginLeft: sidebar ? '250px' : '100px',
                    width: sidebar ? 'calc(100% - 250px)' : 'calc(100% - 100px)'
                }}
            >
                <div className="modern-settings-header-with-actions">
                    <div className="modern-settings-title-section">
                        <h1>{t('deliveryTypes.title', 'Configuracoes de Entrega e Retirada')}</h1>
                        <p className="subtitle">{t('deliveryTypes.subtitle', 'Configure quais tipos de servico sua loja oferece aos clientes')}</p>
                    </div>
                    <div className="modern-settings-actions">
                        <button
                            type="button"
                            className="modern-btn modern-btn-secondary"
                            onClick={handleBack}
                        >
                            <SlActionUndo />
                            {t('common:buttons.back')}
                        </button>
                    </div>
                </div>

                <div className="modern-settings-content">
                    {formContent}
                </div>
            </div>
        </PermissionGate>
    );
};

export default FormasTiposEntrega;
